import { useEffect, useRef } from 'react';

import type { ReaderState } from '../types';
import { PageImage } from '../components/PageImage';
import { usePinchZoom } from '../hooks/usePinchZoom';
import { pageImageTransformFromRef } from '../pageTransforms';
import { cn } from '@/lib/utils';

export interface HorizontalSwipeProps {
  state: ReaderState;
  /** Page-changed callback used by Reader to debounce history writes. */
  onPageVisible?: (page: number) => void;
  onToggleChrome?: () => void;
}

const SWIPE_THRESHOLD_PX = 60;

/**
 * One page at a time, swipe (or arrow-key, or tap-zone) to advance. Tap
 * zones split the viewport into three vertical bands:
 *
 *   |  left third  |  middle third  |  right third  |
 *   |  prev page   |  toggle UI*    |  next page    |
 *
 * (* the toolbar/progress always show; this center zone is reserved for a
 * future "toggle chrome" affordance — kept inert for now.)
 *
 * Swipe direction respects the reader `direction` pref (LTR = swipe left to
 * go forward; RTL = swipe right to go forward).
 *
 * Pinch-zoom and double-tap are handled by `usePinchZoom`.
 */
export function HorizontalSwipe({ state, onPageVisible, onToggleChrome }: HorizontalSwipeProps) {
  const { pages, currentPage, setCurrentPage, prefs, resolveUrl, invalidateUrl } = state;
  const totalPages = pages.length;
  const onPageVisibleRef = useRef(onPageVisible);
  onPageVisibleRef.current = onPageVisible;

  useEffect(() => {
    onPageVisibleRef.current?.(currentPage);
  }, [currentPage]);

  const zoom = usePinchZoom();
  const touchStartRef = useRef<{ x: number; y: number; t: number } | null>(null);

  function go(delta: number) {
    const next = Math.max(1, Math.min(totalPages, currentPage + delta));
    if (next !== currentPage) {
      zoom.reset();
      setCurrentPage(next);
    }
  }

  function forwardDir(): number {
    return prefs.direction === 'rtl' ? -1 : 1;
  }

  function handleClick(e: React.MouseEvent<HTMLDivElement>) {
    // Only run the tap-zone fallback for primary button clicks that did not
    // start a drag (we leave that to pointer handlers).
    if (zoom.zoomed) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const third = rect.width / 3;
    if (x < third) go(-forwardDir());
    else if (x > rect.width - third) go(forwardDir());
    else onToggleChrome?.();
  }

  function handleTouchStart(e: React.TouchEvent) {
    if (zoom.zoomed) return;
    const t = e.touches[0];
    if (!t) return;
    touchStartRef.current = { x: t.clientX, y: t.clientY, t: performance.now() };
  }

  function handleTouchEnd(e: React.TouchEvent) {
    if (zoom.zoomed) return;
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start) return;
    const end = e.changedTouches[0];
    if (!end) return;
    const dx = end.clientX - start.x;
    const dy = end.clientY - start.y;
    // Reject the gesture if it was mostly vertical or too small.
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX || Math.abs(dy) > Math.abs(dx)) return;
    const swipedRight = dx > 0;
    // LTR: swipe right -> previous page; swipe left -> next page.
    // RTL: swipe right -> next page; swipe left -> previous page.
    if (prefs.direction === 'rtl') {
      go(swipedRight ? 1 : -1);
    } else {
      go(swipedRight ? -1 : 1);
    }
  }

  const ref = pages[currentPage - 1];

  return (
    <div
      className="relative flex h-[calc(100vh-7rem)] w-full select-none items-center justify-center overflow-hidden bg-background"
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div
        className="relative flex h-full w-full items-center justify-center"
        {...zoom.handlers}
      >
        <div
          className="flex h-full w-full items-center justify-center"
          style={zoom.style}
        >
          {ref && (
            <PageImage
              key={`${ref.comicId}-${currentPage}`}
              page={currentPage}
              transform={pageImageTransformFromRef(ref)}
              resolveUrl={() => resolveUrl(currentPage).then((u) => u ?? undefined)}
              onRetry={() => invalidateUrl(currentPage)}
              className={cn(
                'h-full w-full',
                prefs.fit === 'width' && 'h-auto w-full',
                prefs.fit === 'height' && 'h-full w-auto',
              )}
            />
          )}
        </div>
      </div>

      <TapZoneOverlay />
    </div>
  );
}

/**
 * Visual hint of where the tap zones are. Pointer-events: none so it never
 * intercepts. Hidden on hover via opacity so it doesn't get in the way.
 */
function TapZoneOverlay() {
  return (
    <div className="pointer-events-none absolute inset-0 grid grid-cols-3 opacity-0 transition-opacity sm:hover:opacity-100">
      <div className="border-r border-dashed border-border/30" />
      <div />
      <div className="border-l border-dashed border-border/30" />
    </div>
  );
}
